/**
 * The ONE consume-path error classifier.
 *
 * The consume flow used to carry THREE overlapping, hand-rolled error policies:
 *   1. the axios interceptor blanket-alerted EVERY failed consume PUT;
 *   2. serverErrorRouting.shouldReportError gated GET telemetry by raw status;
 *   3. clientErrors.reportAutosaveFailure threshold-alerted checklist autosaves.
 * They disagreed, double-fired, and — worst — told a learner to "reconnect and
 * try again" for outcomes a reconnect can never fix (a closed-assignment review,
 * an access denial, a validation rejection).
 *
 * This module replaces all three guesses with one decision keyed off the
 * backend's authoritative signal: `(httpStatus, detail.error)`. vingapi's
 * exception layer (consume/shared/exceptions.py + the closed/review-mode errors)
 * already emits the ground truth; the frontend must classify off it instead of
 * inferring intent from a bare status code.
 *
 * For every consume outcome the classifier decides three things:
 *   (a) retry?          — is the transport allowed to replay this write?
 *   (b) userMessage     — WHAT the learner should see (reconnect vs. the real reason)
 *   (c) breaker?        — does it count toward the app circuit breaker?
 *
 * It deliberately does NOT decide alert suppression. A failed consume write is
 * ALWAYS alert-worthy: we expect consume writes to succeed and expect the client
 * NOT to write to a closed/denied assignment, so any failed consume PUT — even a
 * terminal 403/409/422 — is an unexpected client-misbehavior signal that must
 * surface. That policy lives at the call site (the apiClient interceptor), decoupled
 * from the user-facing MESSAGE this module governs: a terminal outcome shows the
 * server's real reason (never a "reconnect" lie) yet still raises an ops alert.
 *
 * Pure and dependency-light on purpose (axios only) so both the api layer and the
 * consume UI can share it without a cycle. Presentation text lives in the consume
 * feature (see consumeSaveBanner); this module only names the KIND of message.
 */
import axios, { type AxiosError } from 'axios';
import { getStatus } from '@/connectivity/connectivityController';

/** vingapi#1549: the 409 `detail.error` for a write whose expected consume key differs from the row's. */
export const CONSUME_KEY_MISMATCH_CODE = 'consume_key_mismatch';

/** What the learner should be shown for a consume outcome. */
export type ConsumeUserMessage =
  /** Transient (network / 5xx / 429 / 408): "reconnect and try again", with a retry control. */
  | 'reconnect'
  /** 401: re-authenticate / access-denied (consume is OPTIONAL_AUTH — no forced logout). */
  | 'reauth'
  /** 403: the specific access-denied reason for `detail.error` (not "reconnect"). */
  | 'accessDenied'
  /** 409 assignment_closed: benign review-mode refusal — the review banner IS the context; stay silent. */
  | 'reviewClosed'
  /** 422: the specific business-validation message (not "reconnect"). */
  | 'validation'
  /** 404 on a GET: the content is gone/invalid — "unable to load content". */
  | 'unableToLoad'
  /**
   * 409 consume_key_mismatch (vingreact#1029): the row this page belongs to
   * changed (reassignment, another person signed in on this device). The page
   * stops; its banner is rendered from the page's own flag (1029-b), never from
   * the server's `detail.message`.
   */
  | 'keyMismatch'
  /** 400 / other terminal 4xx: a generic non-reconnect message. */
  | 'generic';

export interface ConsumeErrorDecision {
  /** May the transport replay this request (network/5xx/429/408)? */
  retry: boolean;
  /** Convenience inverse of `retry` — this outcome will never be retried. */
  terminal: boolean;
  /** What the learner should see. */
  userMessage: ConsumeUserMessage;
  /** Count this failure toward the app circuit breaker? Only network + 5xx (server faults). */
  breaker: boolean;
  /** The HTTP status, when there was a response (undefined = network/timeout). */
  status?: number;
  /** The backend `detail.error` code, when present. */
  code?: string;
}

/**
 * The pure core: classify a consume outcome from its `(status, code)` alone.
 * `status === undefined` means no response (network error / timeout). Every row
 * here corresponds to a row of the audit's contract table.
 */
export function classifyConsumeOutcome(
  status: number | undefined,
  code?: string,
): ConsumeErrorDecision {
  const terminal = (d: Omit<ConsumeErrorDecision, 'terminal' | 'status' | 'code'>): ConsumeErrorDecision =>
    ({ ...d, terminal: !d.retry, status, code });

  // No response — network error / timeout. Transient server-fault class.
  if (status === undefined) {
    return terminal({ retry: true, userMessage: 'reconnect', breaker: true });
  }
  // 5xx — server fault. Retry and count toward the breaker.
  if (status >= 500) {
    return terminal({ retry: true, userMessage: 'reconnect', breaker: true });
  }
  // 429 throttle / 408 request-timeout — retry, but NOT a server fault, so it must
  // not trip the breaker (throttling the client is not the app dying).
  if (status === 429 || status === 408) {
    return terminal({ retry: true, userMessage: 'reconnect', breaker: false });
  }
  // 401 — auth. Consume is OPTIONAL_AUTH: no forced logout; the viewer re-auths or
  // is shown the access-denied surface. Terminal; no breaker.
  if (status === 401) {
    return terminal({ retry: false, userMessage: 'reauth', breaker: false });
  }
  // 403 — an access denial (assignment_required / content_not_ready /
  // preview_not_authorized). Show the specific reason, never "reconnect".
  if (status === 403) {
    return terminal({ retry: false, userMessage: 'accessDenied', breaker: false });
  }
  // 409 — a closed assignment. `assignment_closed` (ReviewModeError) is a
  // deliberate review-mode refusal: the ReviewBanner already explains it, so the
  // save surface stays silent (message-wise). Any other 409 is a terminal backstop
  // with a generic message. Whether it alerts is the call site's decision.
  if (status === 409) {
    // #1336 R4: a second `account_merged_retry` (the interceptor already resent
    // once) is a server error like a 500: transient, "reconnect", the queue's
    // backoff redrives it. It is not the app dying, so no breaker.
    if (code === 'account_merged_retry') {
      return terminal({ retry: true, userMessage: 'reconnect', breaker: false });
    }
    if (code === 'assignment_closed') {
      return terminal({ retry: false, userMessage: 'reviewClosed', breaker: false });
    }
    if (code === CONSUME_KEY_MISMATCH_CODE) {
      return terminal({ retry: false, userMessage: 'keyMismatch', breaker: false });
    }
    return terminal({ retry: false, userMessage: 'generic', breaker: false });
  }
  // 422 — a business ValidationError. Show the specific message, never "reconnect".
  if (status === 422) {
    return terminal({ retry: false, userMessage: 'validation', breaker: false });
  }
  // 400 — native validation / ValueError: the client sent something the contract
  // forbids. A generic message for the learner.
  if (status === 400) {
    return terminal({ retry: false, userMessage: 'generic', breaker: false });
  }
  // 404 — content absent / not visible. "unable to load content".
  if (status === 404) {
    return terminal({ retry: false, userMessage: 'unableToLoad', breaker: false });
  }
  // Any other 4xx — terminal and unexpected. Generic message.
  return terminal({ retry: false, userMessage: 'generic', breaker: false });
}

/**
 * Read the backend `detail.error` code off an error body, tolerating both
 * FastAPI's `{"detail": {...}}` wrapper and a raw `{...}` body. Error responses
 * are NOT camelCase-converted by the interceptor, but the `error` key is
 * identical in either casing, so no conversion is needed here.
 */
export function consumeErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = (error as AxiosError).response?.data as { detail?: unknown } | undefined;
  if (!data || typeof data !== 'object') return undefined;
  const detail = (data as { detail?: unknown }).detail ?? data;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const errorCode = (detail as { error?: unknown }).error;
    if (typeof errorCode === 'string' && errorCode.length > 0) return errorCode;
  }
  return undefined;
}

/**
 * Is this failure an EXPECTED refusal the client must not report as a crash?
 * Today only a 409 `consume_key_mismatch` (vingreact#1029): the server logs it
 * at INFO and the page handles it; every other consume failure is reported.
 */
export function isExpectedConsumeRefusal(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  return (error as AxiosError).response?.status === 409 && consumeErrorCode(error) === CONSUME_KEY_MISMATCH_CODE;
}

/**
 * Classify a live consume error (the shape the interceptor / persist path throw).
 * A genuine cancellation is not a consume OUTCOME — callers filter those before
 * classifying (the interceptor after its `axios.isCancel` early-return, the save
 * banner up front) — but we still classify it benignly (terminal, silent) so a
 * stray cancel can never surface a false failure.
 */
export function classifyConsumeError(error: unknown): ConsumeErrorDecision {
  if (axios.isCancel(error)) {
    return { retry: false, terminal: true, userMessage: 'reviewClosed', breaker: false };
  }
  const status = axios.isAxiosError(error) ? (error as AxiosError).response?.status : undefined;
  return classifyConsumeOutcome(status, consumeErrorCode(error));
}

/**
 * Is this refusal an AUTH denial — "we don't know who you are (any more)"?
 *
 * The distinction matters to the persist queue (#133). Every terminal outcome
 * must stop the retry loop, but only this class is fixed by the LEARNER doing
 * something (signing in), which means:
 *   - the unsaved payload is worth RETAINING — the same bytes will land once
 *     they re-authenticate, so discarding it throws away real work;
 *   - the banner must carry a sign-in affordance, not just a message.
 *
 * A 422 validation rejection or a 400 contract error is equally terminal but
 * is about the PAYLOAD: no amount of signing in makes those bytes acceptable,
 * so they are dropped rather than kept forever.
 *
 * 401 is auth by definition. 403 is auth only for the codes vingapi emits when
 * the caller is unidentified (`authentication_required` on a verified-privacy
 * share, `preview_auth_required` on a preview link) — NOT for
 * `assignment_required` / `content_not_ready`, which signing in cannot fix.
 */
const AUTH_DENIED_403_CODES = new Set(['authentication_required', 'preview_auth_required']);

export function isConsumeAuthDenial(decision: ConsumeErrorDecision): boolean {
  if (decision.status === 401) return true;
  return decision.status === 403 && !!decision.code && AUTH_DENIED_403_CODES.has(decision.code);
}

/**
 * The address vingapi says this viewer should sign in as
 * (`detail.login_or_signup_as`), or null. Error bodies are NOT camelCase-converted
 * by the interceptor, so read the snake_case key — and tolerate the camel form in
 * case a caller hands us an already-converted body.
 */
// Re-export from the single classifier (D1). Consume callers continue to
// import from this module unchanged.
export { isTransportFailure } from '@/api/failureClass';

/**
 * Compute the transport-report dimensions for a consume-write report.
 *
 * `exhausted`: true when the retry chain has given up (no more retries).
 * `online`: true when the browser believes it is online AND the connectivity
 * controller has not committed `offline`. Uses `navigator.onLine !== false`
 * (double-negative tolerates undefined in non-browser environments) and
 * `getStatus() !== 'offline'` — `degraded`, `outage`, and `maintenance` are
 * all still "the browser is online" for reporting purposes (D1, D6).
 *
 * This is a pure policy function: the default returns both dimensions
 * unconditionally. To gate on `exhausted && online`, flip this one function.
 */
export function transportReportDims({ willRetry }: { willRetry: boolean }): {
  exhausted: boolean;
  online: boolean;
} {
  return {
    exhausted: !willRetry,
    online: navigator.onLine !== false && getStatus() !== 'offline',
  };
}

export function consumeLoginHint(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data = (error as AxiosError).response?.data as { detail?: unknown } | undefined;
  if (!data || typeof data !== 'object') return null;
  const detail = (data as { detail?: unknown }).detail ?? data;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const body = detail as { login_or_signup_as?: unknown; loginOrSignupAs?: unknown };
  const hint = body.login_or_signup_as ?? body.loginOrSignupAs;
  return typeof hint === 'string' && hint.length > 0 ? hint : null;
}

/**
 * The 409 `required_incomplete` body a checklist submit gets when required
 * answers are still missing (vingapi `RequiredQuestionsIncompleteError`,
 * file-handling plan R3): `{code: "required_incomplete", question_ids: [...]}`
 * at the top level (not wrapped in `detail`). Returns the listed question ids,
 * or null when `error` is anything else. Error bodies are not camelCased.
 */
export function readRequiredIncomplete(error: unknown): number[] | null {
  if (!axios.isAxiosError(error)) return null;
  const response = (error as AxiosError).response;
  if (response?.status !== 409) return null;
  const data = response.data as { detail?: unknown } | undefined;
  if (!data || typeof data !== 'object') return null;
  const body = ((data as { detail?: unknown }).detail ?? data) as { code?: unknown; question_ids?: unknown };
  if (body.code !== 'required_incomplete' || !Array.isArray(body.question_ids)) return null;
  return body.question_ids.filter((id): id is number => typeof id === 'number');
}

/**
 * The refusal details of the checklist submission-status read
 * (`GET /api/view/shares/{stub}/submissions/{id}/`, vingapi#1523 S1). Both are
 * 404s with a plain-string `detail`: `Submission not found` is the ONE body for
 * every miss (unknown id, another contact's or share's submission, a packet
 * share, a viewer that resolves to no contact), `Share not found` an unknown
 * stub. Both are expected answers, not client bugs: the caller keeps today's
 * submit copy and files no crash report (vingreact#989 S2s).
 */
const SUBMISSION_STATUS_MISS_DETAILS: ReadonlySet<string> = new Set([
  'Submission not found',
  'Share not found',
]);

/** Is this error one of the submission-status read's known 404s? */
export function isSubmissionStatusMiss(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const response = (error as AxiosError).response;
  if (response?.status !== 404) return false;
  const detail = (response.data as { detail?: unknown } | undefined)?.detail;
  return typeof detail === 'string' && SUBMISSION_STATUS_MISS_DETAILS.has(detail);
}
